import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Collapse, Divider, IconButton, Stack, TextField, Typography } from "@mui/material";
import { ExpandMore, ExpandLess, StoreMallDirectory } from "@mui/icons-material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { includes, set } from "lodash";
import { storeBudgetTrackerReport } from "data/storeBudgetData";
import { arrType, getColDefaultValue } from "data/globalData";
import RenderInputField from "components/EboStoreV4/CommonComponents/RenderInputFiled/RenderInputFiled";

const formatCurrency = (v) => `Rs. ${Number(v || 0).toLocaleString("en-IN")}`;
const formatL = (v) => `${(Number(v) / 100000).toFixed(1)}L`;
const getShortCodeLabel = (label) => {
  const parts = String(label || "").split("/");
  // if (parts.length <= 2) return parts.join("/");
  // return parts.slice(-2).join("/");
  return parts[1]
};

const StatCard = ({ label, value, sub, color = "#1e40af", bg = "#f0f7ff" }) => (
  <Box sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: bg }}>
    <Typography variant="body2" color="text.secondary" fontSize={12}>{label}</Typography>
    <Typography variant="h6" fontWeight={700} sx={{ color }} lineHeight={1.3}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Box>
);

const computePoSummary = (poDetails = []) => {
  const poMap = new Map();
  const grnMap = new Map();
  let totalGrnWithTax = 0;
  let totalGrnWithoutTax = 0;

  poDetails.forEach((row) => {
    const poNo = row.po_number;
    const grnNo = row.grn_no || row.invoice_no || row.id || poNo;
    const poValue = Number(row.po_value) || 0;
    const poTax = Number(row.po_tax) || 0;
    const invWithTax = Number(row.invoice_value_with_tax) || 0;
    const invWithoutTax = Number(row.invoice_value_without_tax) || 0;
    const status = String(row.item_status || "").toUpperCase();
    const poWithTax = poValue + poTax;

    if (!poMap.has(poNo)) {
      poMap.set(poNo, {
        po_number: poNo,
        po_value: poValue,
        po_tax: poTax,
        po_with_tax: poWithTax,
        material: row.material,
        canceled: 0,
        completed: 0,
      });
    }

    const poEntry = poMap.get(poNo);
    if (status.includes("CANCEL")) {
      poEntry.canceled = Math.max(poEntry.canceled, poWithTax);
    }

    if (status.includes("COMPLET")) {
      const grnKey = `${poNo}__${grnNo}`;
      if (!grnMap.has(grnKey)) {
        grnMap.set(grnKey, {
          po_number: poNo,
          grn_no: grnNo,
          value_with_tax: 0,
          value_without_tax: 0,
          material: row.material,
        });
      }

      const grnEntry = grnMap.get(grnKey);
      grnEntry.value_with_tax += invWithTax;
      grnEntry.value_without_tax += invWithoutTax;
      if (!grnEntry.material && row.material) grnEntry.material = row.material;
    }

    totalGrnWithTax += invWithTax;
    totalGrnWithoutTax += invWithoutTax;
  });

  const grnBreakdown = Array.from(grnMap.values());
  const grnTotalsByPo = grnBreakdown.reduce((acc, item) => {
    acc.set(item.po_number, (acc.get(item.po_number) || 0) + item.value_with_tax);
    return acc;
  }, new Map());

  const distinctPOs = Array.from(poMap.values());
  distinctPOs.forEach((po) => {
    po.completed = grnTotalsByPo.get(po.po_number) || 0;
  });
  const totalPoBase = distinctPOs.reduce((s, p) => s + p.po_value, 0);
  const totalPoTax = distinctPOs.reduce((s, p) => s + p.po_tax, 0);
  const totalPoWithTax = distinctPOs.reduce((s, p) => s + p.po_with_tax, 0);
  const totalCanceled = distinctPOs.reduce((s, p) => s + p.canceled, 0);
  const totalPending = distinctPOs.reduce((s, p) => s + Math.max(p.po_with_tax - p.canceled - p.completed, 0), 0);
  const overallPoAmount = totalPoWithTax - totalCanceled;

  // material breakdown
  const materialMap = new Map();
  poDetails.forEach((row) => {
    const mat = row.material || "Other";
    const val = Number(row.invoice_value_with_tax) || 0;
    materialMap.set(mat, (materialMap.get(mat) || 0) + val);
  });

  return {
    distinctPOs,
    grnBreakdown,
    totalPoBase,
    totalPoTax,
    totalPoWithTax,
    totalCanceled,
    totalPending,
    overallPoAmount,
    totalGrnWithTax,
    totalGrnWithoutTax,
    materialBreakdown: Array.from(materialMap.entries()).map(([name, value]) => ({ name, value })),
    poCount: distinctPOs.length,
    rowCount: poDetails.length,
  };
};

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316"];

const BudgetCard = ({ budget }) => {
  const [open, setOpen] = useState(false);

  const totalBudget = Number(budget?.total_budget) || 0;
  const isSubmitted = budget?.is_submitted === true || budget?.status === "submitted";
  const po = useMemo(() => computePoSummary(budget?.po_details || []), [budget]);

  const remaining = Math.max(totalBudget - po.overallPoAmount, 0);
  const exceeded = po.overallPoAmount > totalBudget ? po.overallPoAmount - totalBudget : 0;
  const utilization = totalBudget ? ((po.overallPoAmount / totalBudget) * 100).toFixed(1) : "0.0";
  const isOver = Number(utilization) > 100;

  const pieData = [
    { id: 0, value: po.overallPoAmount, label: "PO Amount", color: "#3b82f6" },
    { id: 1, value: remaining || 0, label: "Remaining", color: "#10b981" },
    ...(exceeded ? [{ id: 2, value: exceeded, label: "Exceeded", color: "#ef4444" }] : []),
    ...(po.totalCanceled ? [{ id: 3, value: po.totalCanceled, label: "Canceled", color: "#9ca3af" }] : []),
  ].filter((d) => d.value > 0);

  const barData = [
    { label: "Total Budget", value: totalBudget, color: "#3b82f6" },
    { label: "PO (w tax)", value: po.overallPoAmount, color: "#6366f1" },
    { label: "Cancelled", value: po.totalCanceled, color: "#ef4444" },
    { label: "GRN (w tax)", value: po.totalGrnWithTax, color: "#10b981" },
    { label: "Pending", value: po.totalPending, color: "#f59e0b" },
  ];

  const materialPieData = po.materialBreakdown.map((m, i) => ({
    id: i, value: m.value, label: m.name, color: COLORS[i % COLORS.length],
  }));

  return (
    <Box sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden", backgroundColor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
      {/* Header */}
      <Stack
        direction="row" alignItems="center" justifyContent="space-between"
        onClick={() => setOpen((p) => !p)}
        sx={{ px: 2.5, py: 1.75, cursor: "pointer", backgroundColor: open ? "#f0f7ff" : "#fff", "&:hover": { backgroundColor: "#f0f7ff" }, transition: "background 0.2s" }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, backgroundColor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <StoreMallDirectory sx={{ color: "#3b82f6", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>{budget?.project_id}</Typography>
            <Typography variant="caption" color="text.secondary">
              {budget?.city}, {budget?.state} &nbsp;·&nbsp; {budget?.channel} &nbsp;·&nbsp; {budget?.work_type} &nbsp;·&nbsp; {budget?.project}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
          <Chip label={isSubmitted ? "Closed" : "Open"} color={isSubmitted ? "success" : "warning"} size="small" variant="outlined" />
          <Chip
            label={`${utilization}% utilized`} size="small"
            sx={{ backgroundColor: isOver ? "#fee2e2" : "#dbeafe", color: isOver ? "#dc2626" : "#1d4ed8", fontWeight: 600, border: "none" }}
          />
          <Chip label={`${po.poCount} POs · ${po.rowCount} rows`} size="small" variant="outlined" color="default" />
          <Typography variant="body2" fontWeight={700}>{formatCurrency(totalBudget)}</Typography>
          <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Stack>
      </Stack>

      <Collapse in={open}>
        <Divider />
        <Box sx={{ p: 2.5, backgroundColor: "#fafcff" }}>

          {/* Store Info Row */}
          <Stack direction="row" gap={1} flexWrap="wrap" mb={2}>
            {[
              { label: "Store Code", value: budget?.store_code },
              { label: "Work Type", value: budget?.work_type },
              { label: "Opening Date", value: budget?.store_opening_date },
              { label: "Carpet Area", value: `${Number(budget?.carpet_area || 0).toLocaleString()} sq ft` },
              { label: "Std / Sq Ft", value: formatCurrency(budget?.std_per_sq_feet) },
            ].map((item) => (
              <Box key={item.label} sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0" }}>
                <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                <Typography variant="body2" fontWeight={600}>{item.value || "-"}</Typography>
              </Box>
            ))}
          </Stack>

          {/* Financial Summary Cards */}
          <Stack direction="row" gap={1.5} flexWrap="wrap" mb={3}>
            <StatCard label="Total Budget" value={formatCurrency(totalBudget)} color="#1d4ed8" bg="#eff6ff" />
            <StatCard label="PO Amount (w/o tax)" value={formatCurrency(po.totalPoBase)} sub={`Tax: ${formatCurrency(po.totalPoTax)}`} color="#7c3aed" bg="#f5f3ff" />
            <StatCard label="PO Amount (w tax)" value={formatCurrency(po.overallPoAmount)} color="#4f46e5" bg="#eef2ff" />
            <StatCard label="GRN Amount (w tax)" value={formatCurrency(po.totalGrnWithTax)} sub={`w/o tax: ${formatCurrency(po.totalGrnWithoutTax)}`} color="#0f766e" bg="#f0fdf4" />
            <StatCard label="Canceled Amount" value={formatCurrency(po.totalCanceled)} color="#9ca3af" bg="#f9fafb" />
            <StatCard label="Pending Amount" value={formatCurrency(po.totalPending)} color="#d97706" bg="#fffbeb" />
            <StatCard label={isOver ? "Budget Exceeded" : "Budget Remaining"} value={formatCurrency(isOver ? exceeded : remaining)} color={isOver ? "#dc2626" : "#16a34a"} bg={isOver ? "#fef2f2" : "#f0fdf4"} />
          </Stack>

          {/* Charts Row 1 */}
          <Stack direction={{ xs: "column", lg: "row" }} gap={2.5} mb={2.5}>

            {/* Budget Breakdown Pie */}
            <Box sx={{ flex: 1, minWidth: 260, p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff" }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={0.5}>Budget Breakdown</Typography>
              <Typography variant="caption" color="text.secondary">PO vs Remaining vs Exceeded</Typography>
              <PieChart
                series={[{ data: pieData, innerRadius: 50, outerRadius: 85, paddingAngle: 3, cornerRadius: 4, valueFormatter: (item) => formatCurrency(item.value) }]}
                height={260}
                margin={{ top: 10, bottom: 60, left: 10, right: 10 }}
                slotProps={{ legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" }, padding: 0, itemMarkWidth: 8, itemMarkHeight: 8, labelStyle: { fontSize: 11 } } }}
              />
            </Box>

            {/* Material Breakdown Pie */}
            {materialPieData.length > 0 && (
              <Box sx={{ flex: 1, minWidth: 260, p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff" }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={0.5}>Material Breakdown</Typography>
                <Typography variant="caption" color="text.secondary">GRN value by material category</Typography>
                <PieChart
                  series={[{ data: materialPieData, innerRadius: 50, outerRadius: 85, paddingAngle: 3, cornerRadius: 4, valueFormatter: (item) => formatCurrency(item.value) }]}
                  height={260}
                  margin={{ top: 10, bottom: 60, left: 10, right: 10 }}
                  slotProps={{ legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" }, padding: 0, itemMarkWidth: 8, itemMarkHeight: 8, labelStyle: { fontSize: 11 } } }}
                />
              </Box>
            )}

            {/* Budget vs Actuals Bar */}
            <Box sx={{ flex: 2, minWidth: 300, p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff" }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={0.5}>Budget vs Actuals</Typography>
              <Typography variant="caption" color="text.secondary">Comparison of budget, PO and GRN amounts</Typography>
              <BarChart
                xAxis={[{ scaleType: "band", data: barData.map((b) => b.label), tickLabelStyle: { fontSize: 11 } }]}
                series={[{ data: barData.map((b) => b.value), valueFormatter: (v) => formatCurrency(v), color: "#3b82f6",
                  colorMap: { type: "ordinal", colors: barData.map((b) => b.color) },
                }]}
                yAxis={[{ valueFormatter: formatL }]}
                height={230}
                slotProps={{ legend: { hidden: true } }}
                borderRadius={4}
              />
            </Box>
          </Stack>

          {/* Charts Row 2 — PO Level */}
          <Stack direction={{ xs: "column", lg: "row" }} gap={2.5}>

            {/* PO Level Bar Chart: PO Amount vs GRN Amount per PO */}
            <Box sx={{ flex: 2, minWidth: 300, p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", height: 360 }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={0.5}>PO Level — Amount vs GRN</Typography>
              <Typography variant="caption" color="text.secondary">PO amount (with tax) vs total GRN received per PO number</Typography>
                <BarChart
                xAxis={[{ scaleType: "band", data: po.distinctPOs.map((p) => p.po_number), tickLabelStyle: { fontSize: 10, angle: 0 } }]}
                series={[
                  { data: po.distinctPOs.map((p) => p.po_with_tax), label: "PO Amt (w tax)", color: "#6366f1", valueFormatter: (v) => formatCurrency(v) },
                  { data: po.distinctPOs.map((p) => p.canceled), label: "Cancelled", color: "#9ca3af", valueFormatter: (v) => formatCurrency(v) },
                  { data: po.distinctPOs.map((p) => p.completed), label: "GRN Received", color: "#10b981", valueFormatter: (v) => formatCurrency(v) },
                  { data: po.distinctPOs.map((p) => Math.max(p.po_with_tax - p.canceled - p.completed, 0)), label: "Pending", color: "#f59e0b", valueFormatter: (v) => formatCurrency(v) },
                ]}
                yAxis={[{ valueFormatter: formatL }]}
                height={280}
                borderRadius={4}
                slotProps={{ legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" }, padding: 0, itemMarkWidth: 8, itemMarkHeight: 8, labelStyle: { fontSize: 11 } } }}
                margin={{ bottom: 80 }}
              />
            </Box>

            {/* GRN Sunburst — outer: PO, inner: GRN rows */}
            <Box sx={{ flex: 1, minWidth: 300, p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff" }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" mb={0.5}>GRN Sunburst — PO → GRN Rows</Typography>
              <Typography variant="caption" color="text.secondary">Inner ring: PO total · Outer ring: individual GRN invoice amounts</Typography>
              <PieChart
                series={[
                  {
                    data: po.distinctPOs.map((p, i) => ({
                      id: `po-${i}`,
                      value: p.completed,
                      label: p.po_number,
                      color: COLORS[i % COLORS.length],
                    })).filter((d) => d.value > 0),
                    innerRadius: 40,
                    outerRadius: 75,
                    paddingAngle: 2,
                    cornerRadius: 3,
                    valueFormatter: (item) => formatCurrency(item.value),
                    arcLabel: (item) => getShortCodeLabel(item.label),
                    arcLabelMinAngle: 15,
                  },
                  {
                    data: po.grnBreakdown.map((row) => {
                      const poIdx = po.distinctPOs.findIndex((p) => p.po_number === row.po_number);
                      return {
                        id: `${row.po_number}__${row.grn_no}`,
                        value: row.value_with_tax,
                        label: row.grn_no,
                        color: COLORS[(poIdx >= 0 ? poIdx : 0) % COLORS.length] + "bb",
                      };
                    }).filter((d) => d.value > 0),
                    innerRadius: 80,
                    outerRadius: 115,
                    paddingAngle: 1,
                    cornerRadius: 2,
                    valueFormatter: (item) => formatCurrency(item.value),
                    // arcLabel: (item) => item.label.split("/").pop(),
                    arcLabel: (item) => getShortCodeLabel(item.label),
                    arcLabelMinAngle: 20,
                  },
                ]}
                height={360}
                margin={{ top: 40, bottom: 40, left: 80, right: 80 }}
                slotProps={{ legend: { hidden: true } }}
                sx={{
                  "& .MuiChartsArcLabel-root": { fontSize: 10, fontWeight: 600, fill: "#1e293b" },
                }}
              />
            </Box>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

const EXAMPLE_DATA = [
  {
    id: "budget-1",
    channel: "EBO",
    work_type: "RENOVATION",
    store_name: "THOR-SS-KOLKATA",
    store_code: "THOR023",
    project: "THOR",
    city: "KOLKATA",
    state: "WEST BENGAL",
    store_opening_date: "2026-04-07",
    carpet_area: "0654",
    std_per_sq_feet: "06783",
    total_budget: 4436082,
    is_submitted: false,
    email: "amitp@thehouseofrare.com",
    po_details: [
      { id: "RR/PO/07790/25-26-WH/GRN/13010/25-26-WRMS/25-26/1091", material: "FIXTURE", vendor_name: "Wave Rider Marketing Solutions", po_number: "RR/PO/07790/25-26", po_remark: null, po_date: "2025-09-30", po_value: 114000, po_tax: 20520, invoice_no: "WRMS/25-26/1091", invoice_date: "2025-10-11", invoice_value_without_tax: 114000, invoice_value_with_tax: 134520, grn_date: "2025-11-06", grn_no: "WH/GRN/13010/25-26", store_name: "THOR-SS-KOLKATA", site_code: "THOR023", item_status: "COMPLETED", barcode: "RR257556" },
      { id: "RR/PO/08286/25-26-WH/GRN/12901/25-26-WRMS/25-26/1129", material: "FIXTURE", vendor_name: "Wave Rider Marketing Solutions", po_number: "RR/PO/08286/25-26", po_remark: null, po_date: "2025-10-16", po_value: 337500, po_tax: 60750, invoice_no: "WRMS/25-26/1129", invoice_date: "2025-10-18", invoice_value_without_tax: 337500, invoice_value_with_tax: 398250, grn_date: "2025-11-05", grn_no: "WH/GRN/12901/25-26", store_name: "THOR-SS-KOLKATA", site_code: "THOR023", item_status: "COMPLETED", barcode: "RR267797" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 10880, invoice_value_with_tax: 12838.4, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR245063" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-1", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 512, invoice_value_with_tax: 604.16, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR245064" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-2", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 54800, invoice_value_with_tax: 64664, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR241854" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-3", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 63000, invoice_value_with_tax: 78624, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR241852" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-4", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 8500, invoice_value_with_tax: 10030, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR245066" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-5", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 37100, invoice_value_with_tax: 45164, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR241859" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-6", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 540, invoice_value_with_tax: 637.2, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR245062" },
    ],
  },
];

const initial_state = () => {
    const obj = {};

    storeBudgetTrackerReport?.forEach((attr) => {
      obj[attr.key] = getColDefaultValue(attr);
    });
    return obj;
  };

export default function StoreBudgetReport() {
  const [budgetList, setBudgetList] = useState([]);
  const [storeFilter, setStoreFilter] = useState(initial_state());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [storeData, setStoreData] = useState([]);
  const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

  const getStoreBudget = async () => {
      try {
        setLoadingOpen(true);
        if (["SELECT", ""].includes(storeFilter?.store_name)) {
          throw new Error("Please select a valid store name");
        }
        const data = {
            "store_name": storeFilter?.store_name || "",
            "start_date": storeFilter?.start_date || "",
            "end_date": storeFilter?.end_date || "",
          }
        await pythonVmInstance({
          url: `/store-budget-tracker/report/get-store-budget`,
          params: data,
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        }).then((res) => {
          setBudgetList(res?.data || []);
          setLoadingOpen(false);
          setSnackBarOpen(true);
          setSnackBar({
            ...snackBar,
            message: "successfully fetched the report",
            severity: "success",
            anchorOrigin: { horizontal: "center", vertical: "top" },
          });
        });
      } catch (error) {
        setSnackBarOpen(true);
        setLoadingOpen(false);
        setSnackBar({
          ...snackBar,
          message: error.message ? error.message : "Failed to fetch report",
          severity: "error",
          anchorOrigin: { horizontal: "center", vertical: "top" },
        });
      }
    };
    const getCreatedStoreBudgetStores = async () => {
        try {
          setLoadingOpen(true);
          await pythonVmInstance({
            url: `/store-budget-tracker/report/get-budgeted-store`,
            headers: {
              "Content-Type": "application/json",
            },
            method: "GET",
          }).then((res) => {
            setStoreData(res?.data);
            setLoadingOpen(false);
          });
        } catch (error) {
          setSnackBarOpen(true);
          setLoadingOpen(false);
          setSnackBar({
            ...snackBar,
            message: "Failed to download file",
            severity: "error",
            anchorOrigin: { horizontal: "center", vertical: "top" },
          });
        }
      };

  useEffect(() => {getCreatedStoreBudgetStores() }, []);

  const filtered = useMemo(() => budgetList.filter((b) => {
    const matchStore = !storeFilter || b?.store_name?.toLowerCase().includes(storeFilter?.store_name?.toLowerCase());
    const createdAt = b?.store_opening_date ? new Date(b.store_opening_date) : null;
    const matchFrom = !fromDate || (createdAt && createdAt >= new Date(fromDate));
    const matchTo = !toDate || (createdAt && createdAt <= new Date(toDate + "T23:59:59"));
    return matchStore && matchFrom && matchTo;
  }), [budgetList, storeFilter, fromDate, toDate]);

  const summary = useMemo(() => {
    const totalBudget = filtered.reduce((s, b) => s + (Number(b?.total_budget) || 0), 0);
    const totalPoAmount = filtered.reduce((s, b) => {
      const po = computePoSummary(b?.po_details || []);
      return s + po.overallPoAmount;
    }, 0);
    return {
      total: filtered.length,
      open: filtered.filter((b) => !b?.is_submitted && b?.status !== "submitted").length,
      closed: filtered.filter((b) => b?.is_submitted || b?.status === "submitted").length,
      totalBudget,
      totalPoAmount,
      utilization: totalBudget ? ((totalPoAmount / totalBudget) * 100).toFixed(1) : "0.0",
    };
  }, [filtered]);

  const selectedStore = storeData
    ?.map((st) => ({
      name: st.store_name,
      key: st.store_name,
    }));

  return (
    <Box padding={2}>
      <Typography variant="h5" fontSize={24} textTransform="uppercase" mb={2}>Store Budget Report</Typography>

      {/* Filters */}
     <Stack direction="row" alignItems="center" marginBottom={2} flexWrap="wrap" gap={1}>
          {storeBudgetTrackerReport?.map((row) => {
            let option_data = row.option_data ? row.option_data : {};
            if (row?.key === "store_name") {
              option_data = {
                type: arrType,
                list: selectedStore,
              };
            }
            return (
              <RenderInputField
                key={row.key}
                column={{ ...row, option_data }}
                data={storeFilter}
                setData={setStoreFilter}
                width="24%"
                selectOptObjName={"option_data"}
                // disabled={isSubmitted}
              />
            );
          })}
          <Stack marginTop="21px">
            <Button variant="contained" color="primary" onClick={getStoreBudget} sx={{ height: 40, mt: { xs: 1, sm: 0 } }}>Fetch Report</Button>
          </Stack>
        </Stack>

      {/* Summary Cards */}
      <Stack direction="row" gap={1.5} flexWrap="wrap" mb={2.5}>
        <StatCard label="Total Budgets" value={summary.total} color="#1d4ed8" bg="#eff6ff" />
        <StatCard label="Open" value={summary.open} color="#d97706" bg="#fffbeb" />
        <StatCard label="Closed" value={summary.closed} color="#16a34a" bg="#f0fdf4" />
        <StatCard label="Total Budget Value" value={formatCurrency(summary.totalBudget)} color="#7c3aed" bg="#f5f3ff" />
        <StatCard label="Total PO Amount" value={formatCurrency(summary.totalPoAmount)} sub={`${summary.utilization}% utilized`} color="#0f766e" bg="#f0fdf4" />
      </Stack>

      {/* Budget Cards */}
      <Stack gap={1.5}>
        {filtered.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography color="text.secondary">No store budgets found for the selected filters.</Typography>
          </Box>
        ) : (
          filtered.map((budget) => <BudgetCard key={budget.id} budget={budget} />)
        )}
      </Stack>
    </Box>
  );
}
